import { SeoAudit, SeoAuditStructure, SeoAuditSections, SeoAuditImportantFix, SeoAuditCategoryStatusCounts, SeoAuditCategoriesScores } from './types';

export function extractSeoAuditData(seoAudit: SeoAudit, auditCategories: SeoAuditStructure) {
    const auditImportantFixes = getAuditImportantFixes(seoAudit.sections);
    const categorizedSections = mapAuditSectionsToCategories(seoAudit.sections, auditCategories);
    const categorizedSectionsCounts = getAuditCategoriesStatusCounts(seoAudit.sections, categorizedSections);
    const categoriesScores = getAuditCategoriesScores(seoAudit.sections, categorizedSections);

    return {
        sections: seoAudit.sections,
        createdAt: seoAudit.created_at,
        overview: seoAudit.overview,

        url: seoAudit.url,
        auditImportantFixes,
        categorizedSections,
        categorizedSectionsCounts,
        categoriesScores,
    };
}

function getAuditImportantFixes(auditSections: SeoAuditSections): SeoAuditImportantFix[] {
    return Object.entries(auditSections)
        .filter(([sectionKey, section]) => section.status === 'failed' && section.relevance > 0 && section.recommendation)
        .map(([sectionKey, section]) => ({
            section_key: sectionKey,
            recommendation: section.recommendation,
            relevance: section.relevance,
        }))
        .sort((a, b) => (a.relevance < b.relevance ? 1 : -1));
}

function mapAuditSectionsToCategories(auditSections: SeoAuditSections, auditCategories: SeoAuditStructure): Partial<SeoAuditStructure> {
    let categories: Partial<SeoAuditStructure> = {};

    Object.entries(auditCategories).forEach(([categoryName, sectionsDetails]) => {
        let existingAuditSections = sectionsDetails.filter((sectionDetails) => sectionDetails.key in auditSections);

        if (existingAuditSections.length > 0) {
            categories[categoryName as keyof SeoAuditStructure] = existingAuditSections;
        }
    });

    return categories;
}

function getAuditCategoriesStatusCounts(auditSections: SeoAuditSections, auditCategories: Partial<SeoAuditStructure>): SeoAuditCategoryStatusCounts {
    let categoriesStatusCounts: SeoAuditCategoryStatusCounts = {};

    Object.entries(auditCategories).forEach(([categoryName, sectionsDetails]) => {
        sectionsDetails.forEach((sectionDetails) => {
            const section = auditSections[sectionDetails.key];

            if (!(categoryName in categoriesStatusCounts)) {
                categoriesStatusCounts[categoryName] = {
                    passed: 0,
                    warning: 0,
                    failed: 0,
                };
            }

            categoriesStatusCounts[categoryName][section.status]++;
        });
    });

    return categoriesStatusCounts;
}

function getAuditCategoriesScores(auditSections: SeoAuditSections, auditCategories: Partial<SeoAuditStructure>): SeoAuditCategoriesScores {
    let categoriesStatusScores: SeoAuditCategoriesScores = {};

    const modifiers = {
        passed: 1,
        warning: 0.5,
        failed: 0,
    };

    Object.entries(auditCategories).forEach(([categoryName, sectionsDetails]) => {
        categoriesStatusScores[categoryName] = {
            score: 0,
        };

        let maxPoints = 0;
        let categoryPoints = 0;

        sectionsDetails.forEach((sectionDetails) => {
            const section = auditSections[sectionDetails.key];

            const relevance = section.relevance;
            const status = section.status;
            maxPoints += relevance;
            categoryPoints += relevance * modifiers[status];
        });

        categoriesStatusScores[categoryName].score = maxPoints !== 0 ? Math.round((categoryPoints * 100) / maxPoints) : 0;
    });

    return categoriesStatusScores;
}
